import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale, SUPPORTED_LANGS, LANG_LABELS } from '../hooks/useLocale.jsx';
import { replayTour } from '../components/Onboarding.jsx';
import { useCredits, useCreditConfig } from '../services/credits-service.js';
import { BillingService } from '../services/billing-service.js';
import { PLANS } from '../config/billing.js';
import { ProfileService, DEFAULT_DISPLAY_NAME } from '../services/profile-service.js';
import { AuthService } from '../services/auth-service.js';
import { isNativeApp } from '../services/platform-service.js';
// presentPaywall 은 더 이상 호출 안 함 (신규 구독 판매 중단). Customer Center 는
// 기존 결제자가 스토어에서 갱신/취소하려면 여전히 필요하므로 유지.
import { presentCustomerCenter } from '../services/revenuecat-ui.js';
import { useEffectivePlan } from '../hooks/useEffectivePlan.js';
import { DeleteAccountModal } from '../components/DeleteAccountModal.jsx';
import { analytics, logEvent } from '../firebase.js';

function formatDate(ts, locale) {
  if (!ts) return null;
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(locale || undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

export function Account({ user, onSignInRequest, onCreditsClick }) {
  const { t, lang, setLang } = useLocale();
  const navigate = useNavigate();
  const credits = useCredits();
  const creditCfg = useCreditConfig();
  const [subscription, setSubscription] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // 커스텀 스타일 커뮤니티 공개 토글 (기본 ON) — users 문서에서 읽는다.
  const [styleShare, setStyleShare] = useState(true);
  // Effective Pro 판정 — Firestore plan + 14일 sign-up trial + RC SDK entitlement
  // 세 소스 통합. 자세한 정책은 useEffectivePlan.js 참고.
  // loading 도 받음 — 신규 가입 직후 server initializeUser 가 doc 작성 전에
  // Free plan 으로 잠시 잘못 표시되는 race condition 방지용.
  const { isPro, isProPaid, isProTrial, trialDaysLeft, loading: planLoading } = useEffectivePlan();

  const isLoggedIn = user && !user.isAnonymous;

  useEffect(() => {
    if (!isLoggedIn) return;
    const unsub = BillingService.subscribeToSubscription(user.uid, setSubscription);
    return unsub;
  }, [isLoggedIn, user?.uid]);

  useEffect(() => {
    if (!isLoggedIn) return;
    let stale = false;
    ProfileService.getStyleShare(user.uid).then((v) => { if (!stale) setStyleShare(v); });
    return () => { stale = true; };
  }, [isLoggedIn, user?.uid]);

  useEffect(() => {
    if (!isLoggedIn) return;
    return ProfileService.subscribeByUid(user.uid, setProfile);
  }, [isLoggedIn, user?.uid]);

  // 익명 사용자가 Settings (account) 페이지 진입 = 명백한 로그인 의도. 자동으로
  // onSignInRequest 트리거 → App.jsx 의 openSignIn → Welcome 우회 (trial 강조).
  // user === undefined (auth resolving) 단계엔 skip.
  useEffect(() => {
    if (user === undefined) return;
    if (isLoggedIn) return;
    onSignInRequest?.();
  }, [user, isLoggedIn, onSignInRequest]);

  const handleSignOut = async () => {
    await AuthService.signOut();
    navigate('/');
  };

  if (!isLoggedIn) {
    return (
      <main className="main">
        <div className="empty-state">
          <i className="material-symbols-rounded empty-icon">account_circle</i>
          <h2>{t('signInToSee')}</h2>
          <p>{t('signInToSeeDesc')}</p>
          <button className="btn btn-primary" onClick={() => onSignInRequest?.()}>
            <i className="material-symbols-rounded">login</i>
            {t('signIn')}
          </button>
        </div>
      </main>
    );
  }

  // Effective plan = Pro (구독 OR trial OR RC entitlement) / Free.
  const effectivePlanId = isPro ? 'pro' : (subscription?.plan || 'free');
  const plan = PLANS.find((p) => p.id === effectivePlanId) || PLANS[0];
  const renewsAt = formatDate(subscription?.currentPeriodEnd, lang);

  const handleManageBilling = async () => {
    setPortalError(null);
    setPortalLoading(true);
    try {
      await BillingService.openBillingPortal();
    } catch (err) {
      setPortalError(err.code || err.message || 'PORTAL_FAILED');
    } finally {
      setPortalLoading(false);
    }
  };

  const hasStripeCustomer = Boolean(subscription?.stripeCustomerId);

  // 계정 삭제 안내문 분기 — 실제로 active 구독이 있는 경로만 노출. **플랫폼 무관**:
  // 사용자가 iOS / Android 에서 native IAP 구독 후 데스크톱 웹에서 계정 삭제하는
  // 경우, 또는 웹에서 Stripe 구독 후 모바일 앱에서 삭제하는 경우 모두 정확히 안내.
  // 판단은 Firestore 의 결제 marker (subscription doc 의 필드) 기반.
  //   - stripeSubscriptionId 가 있고 active → Stripe (deleteAccount 가 자동 cancel)
  //   - iosProductId 가 있고 active → native IAP (앱이 못 끔 → 사용자 안내)
  //   ※ Android 의 RC 이벤트도 같은 iosProductId 필드에 product 가 들어옴 (webhook
  //     필드명만 iOS-era 잔재 — 의미적으로는 "native IAP product id").
  // Native 디바이스에서 본인 IAP 구독 확인은 RC SDK 가 추가 fallback (webhook 지연 대비).
  const ACTIVE_STATUSES = new Set(['active', 'trialing', 'past_due']);
  const subActive = ACTIVE_STATUSES.has(subscription?.status);
  const hasWebSubscription = Boolean(subscription?.stripeSubscriptionId && subActive);
  const hasAppleSubscription = Boolean(
    (subscription?.iosProductId && subActive) ||
    (isNativeApp() && isProPaid)
  );

  return (
    <main className="main account-page">
      {/* 페이지 제목은 mobile-header 가 표시 (데스크톱에선 .header 의 워드마크).
         별도 h1 은 모바일에서 같은 글자 두 번 보이게 하므로 생략. */}

      {/* 크레딧 카드 — 구독을 접고 전원 무료 + 크레딧으로 바꾼 뒤의 기본 화면.
         매일 리셋되는 daily 와 쌓이는 bonus 를 나눠 보여준다: 사용자가 "왜
         어제 받은 초대 보상이 그대로 있지?" 를 묻지 않아도 되게.
         이미 결제한 Pro 사용자 (grandfathered) 는 아래 구독 카드로 빠진다.
         planLoading 중에는 렌더 안 함 — 신규 가입 직후 잠시 잘못 표시되는
         깜빡임 방지. */}
      {planLoading ? null : plan.id === 'free' ? (
        <section className="account-card account-credits-card">
          <div className="account-card-header">
            <h2>{t('accountCreditsTitle')}</h2>
            <span className="account-credits-total">{credits.loading ? '—' : credits.credits}</span>
          </div>
          <div className="account-credits-breakdown">
            <div className="account-credits-row">
              <i className="material-symbols-rounded">refresh</i>
              <span className="account-credits-row-label">{t('creditsDailyLabel')}</span>
              <span className="account-credits-row-value">
                {credits.loading ? '—' : `${credits.dailyRemaining} / ${creditCfg.daily}`}
              </span>
            </div>
            <div className="account-credits-row">
              <i className="material-symbols-rounded">card_giftcard</i>
              <span className="account-credits-row-label">{t('creditsBonusLabel')}</span>
              <span className="account-credits-row-value">
                {credits.loading ? '—' : credits.bonus}
              </span>
            </div>
          </div>
          <p className="account-card-hint">{t('creditsResetHint', { n: creditCfg.daily })}</p>
          {/* 충전 — 앱은 스토어 결제, 웹은 Stripe (CreditModal 이 가른다). 잔액 칩을 눌러야만
              팩이 보이던 2.3.0 첫 빌드에선 살 수 있다는 걸 아무도 몰랐다. */}
          <button
            type="button"
            className="account-plan-upsell-cta"
            onClick={() => { logEvent(analytics, 'pack_entry', { surface: 'account' }); onCreditsClick?.(); }}
          >
            <i className="material-symbols-rounded" aria-hidden="true">bolt</i>
            {t('creditsTopUpCta')}
          </button>
          <button
            type="button"
            className="account-plan-upsell-cta is-secondary"
            onClick={() => navigate('/invite')}
          >
            {t('creditsInviteCta', { n: creditCfg.inviteReward })}
          </button>
        </section>
      ) : (
        <section className="account-card">
          <div className="account-card-header">
            <h2>{isProTrial ? t('accountPlanTrialTitle') : t('accountPlanProTitle')}</h2>
            <span className={`account-plan-badge plan-${plan.id}${isProTrial ? ' is-trial' : ''}`}>
              {isProTrial ? t('proTrialBadge') : t(plan.nameKey)}
            </span>
          </div>

          {/* Trial 사용자 — 남은 일수만. 신규 가입 trial 부여는 중단했으므로 여기
             도달하는 건 전환 전에 가입한 사용자뿐이고, 구독 판매를 접었으니
             업그레이드 CTA 는 띄우지 않는다. trial 이 끝나면 자동으로 위쪽
             크레딧 카드로 넘어간다. */}
          {isProTrial && (
            <div className="account-status-row">
              <i className="material-symbols-rounded">timer</i>
              <span>{t('proTrialDaysLeft', { n: trialDaysLeft })}</span>
            </div>
          )}

          {/* 진짜 유료 구독자만 — renew/cancel/manage 노출. */}
          {isProPaid && (
            <>
              {subscription?.status && subscription.status !== 'active' && (
                <div className="account-status-row">
                  <i className="material-symbols-rounded">info</i>
                  <span>{t('subStatus_' + subscription.status) || subscription.status}</span>
                </div>
              )}

              {renewsAt && (
                <div className="account-status-row">
                  <i className="material-symbols-rounded">event</i>
                  <span>
                    {subscription?.status === 'trialing'
                      ? (subscription?.cancelAtPeriodEnd
                          ? t('trialCancelEndsAt', { date: renewsAt })
                          : t('trialEndsAt', { date: renewsAt }))
                      : subscription?.cancelAtPeriodEnd
                        ? t('subEndsAt', { date: renewsAt })
                        : t('subRenewsAt', { date: renewsAt })}
                  </span>
                </div>
              )}

              {!isNativeApp() && hasStripeCustomer && (
                <div className="account-actions">
                  <button className="btn btn-secondary" onClick={handleManageBilling} disabled={portalLoading}>
                    {portalLoading ? t('loading') : t('manageBilling')}
                  </button>
                </div>
              )}
              {/* Native (iOS / Android) — RC IAP 로 Pro 인 경우: Customer Center
                 (스토어 식 갱신/취소). 웹에서 Stripe 로 결제한 경우엔 스토어
                 정책상 외부 링크 직접 열 수 없어 안내 텍스트만. */}
              {isNativeApp() && (
                <div className="account-actions">
                  <button className="btn btn-secondary" onClick={() => presentCustomerCenter()}>
                    {t('manageBilling')}
                  </button>
                </div>
              )}
              {isNativeApp() && !isProPaid && hasStripeCustomer && (
                <p className="account-card-hint" style={{ marginTop: '0.75rem' }}>
                  {t('manageBillingWebHint')}
                </p>
              )}
              {portalError && (
                <p className="account-error">{t('errBillingGeneric')}</p>
              )}
            </>
          )}
        </section>
      )}

      {/* Language — 자주 안 바꾸는 설정. */}
      <section className="account-card account-card-language">
        <h2 className="account-card-language-title">{t('language')}</h2>
        {/* 5개 언어부터는 pill 줄이 아니라 드랍다운. 기본값은 브라우저 언어
           자동 감지 (useLocale detectLang, 미지원 언어면 영어). */}
        <select
          className="account-lang-select"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
        >
          {SUPPORTED_LANGS.map(l => (
            <option key={l} value={l}>{LANG_LABELS[l]}</option>
          ))}
        </select>
        <p className="account-card-hint" style={{ marginTop: '0.625rem' }}>{t('languageHint')}</p>
      </section>

      {/* 커스텀 스타일 커뮤니티 공개 (STYLE_GALLERY.md) — 기본 ON, opt-out */}
      {isLoggedIn && (
        <section className="account-card">
          <label className="account-toggle-row">
            <span>
              <span className="account-toggle-title">{t('styleShareTitle')}</span>
              <span className="account-card-hint">{t('styleShareDesc')}</span>
            </span>
            <input
              type="checkbox"
              checked={styleShare}
              onChange={async (e) => {
                const next = e.target.checked;
                setStyleShare(next);
                try { await ProfileService.updateStyleShare(next); } catch { setStyleShare(!next); }
              }}
            />
          </label>
        </section>
      )}

      <nav className="account-menu-list" aria-label="More">
        {/* 프로필 진입은 여기 하나만 둔다. My 허브의 Profile 탭은 뺐지만
           (공개 피드를 접어 앱 안에서 할 일이 거의 없어졌다) 자기 프로필을
           보거나 공유하려면 들어갈 길은 있어야 한다. */}
        {profile?.handle && (
          <button type="button" className="account-menu-item" onClick={() => navigate(`/u/${profile.handle}`)}>
            <i className="material-symbols-rounded">person</i>
            <span className="account-menu-item-label">{t('profile')}</span>
            <span className="account-menu-item-value">@{profile.handle}</span>
            <i className="material-symbols-rounded account-menu-item-chevron">chevron_right</i>
          </button>
        )}
        {/* 크레딧 모델로 돌아오면서 초대가 유일한 증량 수단이 됐다 — 메뉴로 복귀.
           promo 코드 입력도 같은 페이지에 있다. */}
        <button type="button" className="account-menu-item" onClick={() => navigate('/invite')}>
          <i className="material-symbols-rounded">group_add</i>
          <span className="account-menu-item-label">{t('inviteMenu')}</span>
          <i className="material-symbols-rounded account-menu-item-chevron">chevron_right</i>
        </button>
        <button type="button" className="account-menu-item" onClick={() => navigate('/account/blocked')}>
          <i className="material-symbols-rounded">block</i>
          <span className="account-menu-item-label">{t('blockedUsersMenu')}</span>
          <i className="material-symbols-rounded account-menu-item-chevron">chevron_right</i>
        </button>
        <button type="button" className="account-menu-item" onClick={replayTour}>
          <i className="material-symbols-rounded">tour</i>
          <span className="account-menu-item-label">{t('tourReplay')}</span>
          <i className="material-symbols-rounded account-menu-item-chevron">chevron_right</i>
        </button>
        <button type="button" className="account-menu-item" onClick={() => navigate('/support')}>
          <i className="material-symbols-rounded">help_outline</i>
          <span className="account-menu-item-label">{t('support')}</span>
          <i className="material-symbols-rounded account-menu-item-chevron">chevron_right</i>
        </button>
        <button type="button" className="account-menu-item" onClick={() => navigate('/privacy')}>
          <i className="material-symbols-rounded">shield</i>
          <span className="account-menu-item-label">{t('privacy')}</span>
          <i className="material-symbols-rounded account-menu-item-chevron">chevron_right</i>
        </button>
        <button type="button" className="account-menu-item" onClick={() => navigate('/terms')}>
          <i className="material-symbols-rounded">gavel</i>
          <span className="account-menu-item-label">{t('terms')}</span>
          <i className="material-symbols-rounded account-menu-item-chevron">chevron_right</i>
        </button>
        <button type="button" className="account-menu-item" onClick={handleSignOut}>
          <i className="material-symbols-rounded">logout</i>
          <span className="account-menu-item-label">{t('signOut')}</span>
          <i className="material-symbols-rounded account-menu-item-chevron">chevron_right</i>
        </button>
        <button
          type="button"
          className="account-menu-item account-menu-item-danger"
          onClick={() => setShowDeleteModal(true)}
        >
          <i className="material-symbols-rounded">delete_forever</i>
          <span className="account-menu-item-label">{t('deleteAccount')}</span>
          <i className="material-symbols-rounded account-menu-item-chevron">chevron_right</i>
        </button>
      </nav>

      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => navigate('/')}
          hasWebSubscription={hasWebSubscription}
          hasAppleSubscription={hasAppleSubscription}
        />
      )}
    </main>
  );
}
